import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormularioService, GlobalFormGroup, GlobalFormulario } from '@app/services/formulario';
import { isNullOrUndefined, sameObject } from '@app/utiles/javascript';
import { ordenarArray } from '@global-utiles/ordenacion-array';
import { cloneDeep } from 'lodash';
import { NzTableComponent, NzTableQueryParams } from 'ng-zorro-antd/table';
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { GeneralBreadCrumb } from '../general-template';

const FORM_BUSQUEDA: GlobalFormulario<{ busqueda: string }> = {
  datos: {
    busqueda: '',
  },
  updateOn: {
    busqueda: 'change',
  },
};

export interface GeneralTableHeader {
  key: string;
  label: string;
  sort: boolean;
  class?: string;
}
export interface KeyValueSort<T> {
  key: keyof T;
  value: 'ascend' | 'descend';
}
@Component({ standalone: false, selector: 'app-general-table',
  templateUrl: 'general-table.component.html',
  styleUrls: ['../general-template.scss', './general-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralTableComponent<T> implements OnInit, OnDestroy  {
  @Input() breadcrumbs: GeneralBreadCrumb[] = [];
  @Input() title: string = '';

  @Input() loading: boolean;
  formBusqueda: GlobalFormGroup<{ busqueda: string }>;
  private oldData: T[] = [];
  sortData: T[] = [];
  @Input() set data(data: T[]) {
    this.sortData = cloneDeep(data || []);
    this.oldData = cloneDeep(data || []);
  }
  @Input() someError = false;
  @Output() updatedData = new EventEmitter<T[]>();
  @Output() updateData = new EventEmitter<void>();

  @Input() header: GeneralTableHeader[] = [];
  @Input() customPagination: boolean = false;

  @Input() sortValue: KeyValueSort<T> = { key: null, value: null };
  @Input() funcSort: (array: T[], key: keyof T, value: 'ascend' | 'descend') => T[] = (array, key, value) => [...ordenarArray<T>(array, value === 'ascend' ? 1 : -1, key)];
  @Output() callSort = new EventEmitter<KeyValueSort<T>>();
  @Input() searchValue: string = '';
  @Input() showSearchButton = false;
  @Input() funcFilter: (array: T[], text: string) => T[] = (array, text) => array;
  @Output() callFilter = new EventEmitter<string>();

  @Input() totalData: number = 0;
  @Input() pageSize = 15;
  private _pageIndex = 0
  @Input() 
  set pageIndex (pageIndex: number) {
    if(isNullOrUndefined(pageIndex)) return;
    this._pageIndex = pageIndex;
    if (!this.customPagination) this.basicTable.onPageIndexChange(this.pageIndex);
  }
  get pageIndex() {
    return this._pageIndex;
  }
  trackByRow = (index: number, row: T): unknown => (row as any)?.id ?? (row as any)?.key ?? index;
  trackByHeader = (index: number, header: GeneralTableHeader): string | number => header?.key ?? index;
  @Output() changePage = new EventEmitter<number>();
  @Output() changePageSize = new EventEmitter<number>();
  @Output() dbClick = new EventEmitter<T>();

  @Input() titleTemplate: TemplateRef<unknown>;
  @Input() headerTemplate: TemplateRef<unknown>;
  @Input() rowTemplate: TemplateRef<unknown>;

  @ViewChild('basicTable') basicTable: NzTableComponent<T>;
  private readonly destroy$ = new Subject<void>();
  constructor(private readonly formularioService: FormularioService) {}

  onDbClick(row: T): void {
    this.dbClick.emit(row);
  }

  ngOnInit(): void {
    this.formBusqueda = this.formularioService.construirFormulario(FORM_BUSQUEDA);
    this.formBusqueda.controls.busqueda.setValue(this.searchValue);
    this.formBusqueda.controls.busqueda.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        map((text) => text.toLowerCase()),
        takeUntil(this.destroy$)
      )
      .subscribe((text) => {
        this.sortData = this.funcFilter(this.oldData, text);
        this.updatedData.emit(this.sortData);
        this.callFilter.emit(text);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  sort(params: NzTableQueryParams) {
    const keyValue = params.sort.find((keyValue) => keyValue.value !== null) as KeyValueSort<T>;
    let key: keyof T = null;
    let value: 'ascend' | 'descend' = null;
    if (keyValue === undefined) {
      this.sortData = [...this.oldData];
    } else {
      key = keyValue.key;
      value = keyValue.value;
      this.sortData = this.funcSort(this.sortData, key, value);
    }
    if (sameObject(this.sortValue, { key, value })) return false;
    this.updatedData.emit(this.sortData);
    this.callSort.emit({ key, value });
  }
  onChangePage(pageIndex: number) {
    this.changePage.emit(pageIndex);
  }

  onChangePageSize(pageSize: number) {
    this.changePageSize.emit(pageSize);
  }
  onUpdateData() {
    this.updateData.emit();
  }
}
